/**
 * Anti-Gravity Premium Effects Engine
 * Apple-level animations and interactions
 */

(function () {
    'use strict';

    // ============================================
    // Configuration & Settings
    // ============================================
    const config = {
        motionIntensity: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--motion-intensity') || 1),
        glowStrength: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--glow-strength') || 1),
        reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
    };

    // Custom easing curves for luxury feel
    const easing = {
        smooth: 'cubic-bezier(.25,.1,.25,1)',
        bounce: 'cubic-bezier(.68,-.55,.265,1.55)',
        anticipate: 'cubic-bezier(.47,0,.745,.715)',
        cinematic: 'cubic-bezier(.4,0,.2,1)'
    };

    // ============================================
    // Magnetic Cursor Effect
    // ============================================
    class MagneticCursor {
        constructor() {
            this.cursor = null;
            this.cursorDot = null;
            this.magneticElements = [];
            this.mousePos = { x: 0, y: 0 };
            this.cursorPos = { x: 0, y: 0 };

            // Disabled: cursor tracing effect removed for minimal experience
            // if (config.reducedMotion) return;
            // this.init();
            return;
        }

        init() {
            // Create custom cursor
            this.cursor = document.createElement('div');
            this.cursor.className = 'magnetic-cursor';
            this.cursor.innerHTML = '<div class="magnetic-cursor__ring"></div>';
            document.body.appendChild(this.cursor);

            // Track mouse position
            document.addEventListener('mousemove', (e) => this.onMouseMove(e));

            // Find magnetic elements
            this.magneticElements = document.querySelectorAll('[data-magnetic], .btn, button, a');
            this.magneticElements.forEach(el => {
                el.addEventListener('mouseenter', () => this.onMagnetEnter(el));
                el.addEventListener('mouseleave', () => this.onMagnetLeave());
            });

            this.animate();
        }

        onMouseMove(e) {
            this.mousePos.x = e.clientX;
            this.mousePos.y = e.clientY;
        }

        onMagnetEnter(el) {
            this.cursor.classList.add('magnetic-cursor--active');
            el.classList.add('is-magnetic-hover');
        }

        onMagnetLeave() {
            this.cursor.classList.remove('magnetic-cursor--active');
            document.querySelectorAll('.is-magnetic-hover').forEach(el => {
                el.classList.remove('is-magnetic-hover');
            });
        }

        animate() {
            // Smooth cursor follow
            const lerp = 0.15 * config.motionIntensity;
            this.cursorPos.x += (this.mousePos.x - this.cursorPos.x) * lerp;
            this.cursorPos.y += (this.mousePos.y - this.cursorPos.y) * lerp;

            if (this.cursor) {
                this.cursor.style.transform = `translate(${this.cursorPos.x}px, ${this.cursorPos.y}px)`;
            }

            // Magnetic effect on buttons
            this.magneticElements.forEach(el => {
                if (el.classList.contains('is-magnetic-hover')) {
                    const rect = el.getBoundingClientRect();
                    const centerX = rect.left + rect.width / 2;
                    const centerY = rect.top + rect.height / 2;
                    const deltaX = (this.mousePos.x - centerX) * 0.3;
                    const deltaY = (this.mousePos.y - centerY) * 0.3;
                    el.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
                } else {
                    el.style.transform = '';
                }
            });

            requestAnimationFrame(() => this.animate());
        }
    }

    // ============================================
    // Parallax Engine
    // ============================================
    class ParallaxEngine {
        constructor() {
            this.layers = [];
            this.mouseX = 0;
            this.mouseY = 0;

            if (config.reducedMotion) return;
            // Disable parallax on mobile/tablet - breaks iOS position:fixed
            if (window.innerWidth <= 1024 || 'ontouchstart' in window) return;
            this.init();
        }

        init() {
            this.layers = document.querySelectorAll('[data-parallax-layer]');

            document.addEventListener('mousemove', (e) => {
                this.mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
                this.mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
            });

            window.addEventListener('scroll', () => this.onScroll());
            this.animate();
        }

        onScroll() {
            const scrollY = window.scrollY;

            document.querySelectorAll('[data-parallax-scroll]').forEach(el => {
                const speed = parseFloat(el.dataset.parallaxScroll) || 0.5;
                const yPos = -(scrollY * speed * config.motionIntensity);
                el.style.transform = `translateY(${yPos}px)`;
            });
        }

        animate() {
            this.layers.forEach(layer => {
                const depth = parseFloat(layer.dataset.parallaxLayer) || 1;
                const moveX = this.mouseX * depth * 20 * config.motionIntensity;
                const moveY = this.mouseY * depth * 20 * config.motionIntensity;
                layer.style.transform = `translate(${moveX}px, ${moveY}px)`;
            });

            requestAnimationFrame(() => this.animate());
        }
    }

    // ============================================
    // Scroll Reveal Animations
    // ============================================
    class ScrollReveal {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.reveal(entry.target);
                    }
                });
            }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

            document.querySelectorAll('[data-reveal]').forEach(el => {
                el.classList.add('reveal-hidden');
                observer.observe(el);
            });

            // Staggered children
            document.querySelectorAll('[data-reveal-stagger]').forEach(parent => {
                const children = parent.children;
                Array.from(children).forEach((child, i) => {
                    child.classList.add('reveal-hidden');
                    child.style.transitionDelay = `${i * 100}ms`;
                });
                observer.observe(parent);
            });
        }

        reveal(el) {
            const delay = parseFloat(el.dataset.revealDelay) || 0;

            setTimeout(() => {
                el.classList.remove('reveal-hidden');
                el.classList.add('reveal-visible');

                // Reveal children for staggered
                if (el.dataset.revealStagger !== undefined) {
                    Array.from(el.children).forEach(child => {
                        child.classList.remove('reveal-hidden');
                        child.classList.add('reveal-visible');
                    });
                }
            }, delay);
        }
    }

    // ============================================
    // Split Text Animation
    // ============================================
    class SplitTextAnimation {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            document.querySelectorAll('[data-split-text]').forEach(el => {
                this.splitText(el);
            });
        }

        splitText(el) {
            const text = el.textContent;
            const type = el.dataset.splitText || 'chars'; // chars, words, lines
            el.innerHTML = '';

            if (type === 'chars') {
                text.split('').forEach((char, i) => {
                    const span = document.createElement('span');
                    span.className = 'split-char';
                    span.textContent = char === ' ' ? '\u00A0' : char;
                    span.style.animationDelay = `${i * 30}ms`;
                    el.appendChild(span);
                });
            } else if (type === 'words') {
                text.split(' ').forEach((word, i) => {
                    const span = document.createElement('span');
                    span.className = 'split-word';
                    span.textContent = word;
                    span.style.animationDelay = `${i * 80}ms`;
                    el.appendChild(span);
                    el.appendChild(document.createTextNode(' '));
                });
            }
        }
    }

    // ============================================
    // Liquid Text Effect
    // ============================================
    class LiquidText {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            document.querySelectorAll('[data-liquid-text]').forEach(el => {
                el.classList.add('liquid-text');
            });
        }
    }

    // ============================================
    // Premium Product Card Interactions
    // ============================================
    class PremiumProductCards {
        constructor() {
            // Disabled: 3D tilt effect removed for minimal experience
            // if (config.reducedMotion) return;
            // this.init();
            return;
        }

        init() {
            document.querySelectorAll('.product-card, [data-premium-card]').forEach(card => {
                card.addEventListener('mousemove', (e) => this.onCardMove(e, card));
                card.addEventListener('mouseleave', () => this.onCardLeave(card));
            });
        }

        onCardMove(e, card) {
            const rect = card.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const rotateX = (y - centerY) / 20;
            const rotateY = (centerX - x) / 20;

            // Card tilt
            card.style.transform = `perspective(1000px) rotateX(${rotateX * config.motionIntensity}deg) rotateY(${rotateY * config.motionIntensity}deg) translateZ(10px)`;

            // Dynamic shadow
            const shadowX = (x - centerX) / 10;
            const shadowY = (y - centerY) / 10;
            card.style.boxShadow = `${shadowX}px ${shadowY + 20}px 40px rgba(0,0,0,0.15)`;

            // Spotlight effect
            card.style.setProperty('--spotlight-x', `${x}px`);
            card.style.setProperty('--spotlight-y', `${y}px`);
        }

        onCardLeave(card) {
            card.style.transform = '';
            card.style.boxShadow = '';
        }
    }

    // ============================================
    // Gradient Mesh Background
    // ============================================
    class GradientMesh {
        constructor() {
            this.meshes = [];
            this.time = 0;

            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            document.querySelectorAll('[data-gradient-mesh]').forEach(el => {
                this.createMesh(el);
            });
            this.animate();
        }

        createMesh(el) {
            const colors = el.dataset.gradientMesh?.split(',') || ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24'];
            const canvas = document.createElement('canvas');
            canvas.className = 'gradient-mesh-canvas';
            el.appendChild(canvas);

            const ctx = canvas.getContext('2d');
            this.meshes.push({ el, canvas, ctx, colors });
            this.resize();

            window.addEventListener('resize', () => this.resize());
        }

        resize() {
            this.meshes.forEach(mesh => {
                mesh.canvas.width = mesh.el.offsetWidth;
                mesh.canvas.height = mesh.el.offsetHeight;
            });
        }

        animate() {
            this.time += 0.005 * config.motionIntensity;

            this.meshes.forEach(mesh => {
                const { canvas, ctx, colors } = mesh;
                const w = canvas.width;
                const h = canvas.height;

                ctx.clearRect(0, 0, w, h);

                // Create animated gradient blobs
                colors.forEach((color, i) => {
                    const x = w * (0.5 + 0.3 * Math.sin(this.time + i * 2));
                    const y = h * (0.5 + 0.3 * Math.cos(this.time + i * 1.5));
                    const radius = Math.min(w, h) * 0.4;

                    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
                    gradient.addColorStop(0, color);
                    gradient.addColorStop(1, 'transparent');

                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 0, w, h);
                });
            });

            requestAnimationFrame(() => this.animate());
        }
    }

    // ============================================
    // Button Fill Animation
    // ============================================
    class ButtonFillAnimation {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            document.querySelectorAll('.btn-fill, [data-btn-fill]').forEach(btn => {
                const fill = document.createElement('span');
                fill.className = 'btn-fill__layer';
                btn.appendChild(fill);

                btn.addEventListener('mouseenter', (e) => this.onEnter(e, btn, fill));
                btn.addEventListener('mouseleave', () => this.onLeave(fill));
            });
        }

        onEnter(e, btn, fill) {
            const rect = btn.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            fill.style.left = `${x}px`;
            fill.style.top = `${y}px`;
            fill.classList.add('is-filling');
        }

        onLeave(fill) {
            fill.classList.remove('is-filling');
        }
    }

    // ============================================
    // Glow Shadow Effects
    // ============================================
    class GlowEffects {
        constructor() {
            this.init();
        }

        init() {
            document.querySelectorAll('[data-glow]').forEach(el => {
                const color = el.dataset.glow || 'var(--color-accent)';
                const strength = config.glowStrength;
                el.style.setProperty('--glow-color', color);
                el.style.setProperty('--glow-strength', strength);
                el.classList.add('has-glow');
            });
        }
    }

    // ============================================
    // Smooth Scroll Progress
    // ============================================
    class ScrollProgress {
        constructor() {
            this.progressBar = null;
            this.init();
        }

        init() {
            const bar = document.querySelector('[data-scroll-progress]');
            if (bar) {
                this.progressBar = bar;
                window.addEventListener('scroll', () => this.updateProgress());
            }
        }

        updateProgress() {
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = (window.scrollY / scrollHeight) * 100;
            this.progressBar.style.width = `${progress}%`;
        }
    }

    // ============================================
    // Morph Text Animation
    // ============================================
    class MorphText {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            document.querySelectorAll('[data-morph-text]').forEach(el => {
                const words = el.dataset.morphText.split(',');
                this.startMorph(el, words);
            });
        }

        startMorph(el, words) {
            let index = 0;
            el.textContent = words[0];

            setInterval(() => {
                el.classList.add('morph-out');

                setTimeout(() => {
                    index = (index + 1) % words.length;
                    el.textContent = words[index];
                    el.classList.remove('morph-out');
                    el.classList.add('morph-in');

                    setTimeout(() => el.classList.remove('morph-in'), 500);
                }, 500);
            }, 3000);
        }
    }

    // ============================================
    // SVG Draw Animation
    // ============================================
    class SVGDraw {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.draw(entry.target);
                    }
                });
            }, { threshold: 0.5 });

            document.querySelectorAll('[data-svg-draw]').forEach(svg => {
                const paths = svg.querySelectorAll('path, line, circle, rect, polyline');
                paths.forEach(path => {
                    const length = path.getTotalLength ? path.getTotalLength() : 100;
                    path.style.strokeDasharray = length;
                    path.style.strokeDashoffset = length;
                });
                observer.observe(svg);
            });
        }

        draw(svg) {
            const paths = svg.querySelectorAll('path, line, circle, rect, polyline');
            paths.forEach((path, i) => {
                path.style.transition = `stroke-dashoffset 1.5s ${easing.cinematic} ${i * 0.2}s`;
                path.style.strokeDashoffset = '0';
            });
        }
    }

    // ============================================
    // Initialize All Effects
    // ============================================
    document.addEventListener('DOMContentLoaded', function () {
        // Core effects
        new MagneticCursor();
        new ParallaxEngine();
        new ScrollReveal();
        new PremiumProductCards();
        new GlowEffects();
        new ScrollProgress();

        // Text animations
        new SplitTextAnimation();
        new LiquidText();
        new MorphText();

        // Advanced effects
        new GradientMesh();
        new ButtonFillAnimation();
        new SVGDraw();

        // Iconic Features (respect theme settings)
        new CinematicHeadlines();
        new CinematicSections();

        // Check settings before enabling optional features
        if (document.body.dataset.enableFocusMode !== 'false') {
            new ProductFocusMode();
        }
        if (document.body.dataset.showChapterProgress !== 'false') {
            new ChapterProgress();
        }
        if (document.body.dataset.showPersonalityToggle !== 'false') {
            new PersonalityToggle();
        }

        console.log('✨ Anti-Gravity Effects Engine initialized');
        console.log('🎯 Iconic features loaded');
    });

    // ============================================
    // Cinematic Headlines - Word by Word Reveal
    // ============================================
    class CinematicHeadlines {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            document.querySelectorAll('[data-cinematic-headline]').forEach(el => {
                this.splitIntoWords(el);
            });
        }

        splitIntoWords(el) {
            const text = el.textContent.trim();
            const words = text.split(/\s+/);
            el.innerHTML = '';

            words.forEach((word, i) => {
                const span = document.createElement('span');
                span.className = 'word';
                span.textContent = word;
                span.style.animationDelay = `${0.1 + i * 0.1}s`;
                el.appendChild(span);

                // Add space between words
                if (i < words.length - 1) {
                    el.appendChild(document.createTextNode(' '));
                }
            });
        }
    }

    // ============================================
    // Cinematic Sections - Entry Animations
    // ============================================
    class CinematicSections {
        constructor() {
            if (config.reducedMotion) return;
            this.init();
        }

        init() {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                    }
                });
            }, { threshold: 0.15, rootMargin: '0px 0px -100px 0px' });

            document.querySelectorAll('[data-cinematic-section]').forEach(el => {
                observer.observe(el);
            });
        }
    }

    // ============================================
    // Product Focus Mode - DISABLED (User request)
    // ============================================
    class ProductFocusMode {
        constructor() {
            // DISABLED - User doesn't want any highlighting
            return;
        }

        init() { }
        enableFocusMode() { }
        disableFocusMode() { }
    }

    // ============================================
    // Chapter Progress - Scroll Storytelling
    // ============================================
    class ChapterProgress {
        constructor() {
            this.chapters = [];
            this.progressContainer = null;
            this.init();
        }

        init() {
            // Find all sections marked as chapters
            const chapterSections = document.querySelectorAll('[data-chapter]');
            if (chapterSections.length < 2) return; // Need at least 2 chapters

            this.chapters = Array.from(chapterSections);
            this.createProgressUI();
            this.bindScroll();
        }

        createProgressUI() {
            this.progressContainer = document.createElement('nav');
            this.progressContainer.className = 'chapter-progress';
            this.progressContainer.setAttribute('aria-label', 'Page sections');

            this.chapters.forEach((chapter, i) => {
                const dot = document.createElement('button');
                dot.className = 'chapter-progress__dot';
                const chapterName = chapter.dataset.chapter || `Section ${i + 1}`;
                dot.setAttribute('data-chapter-name', chapterName);
                dot.setAttribute('aria-label', `Go to ${chapterName}`);

                dot.addEventListener('click', () => {
                    chapter.scrollIntoView({ behavior: 'smooth' });
                });

                this.progressContainer.appendChild(dot);
            });

            document.body.appendChild(this.progressContainer);
        }

        bindScroll() {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const index = this.chapters.indexOf(entry.target);
                    const dot = this.progressContainer.children[index];

                    if (entry.isIntersecting) {
                        // Update active states
                        Array.from(this.progressContainer.children).forEach((d, i) => {
                            d.classList.remove('chapter-progress__dot--active');
                            if (i < index) {
                                d.classList.add('chapter-progress__dot--passed');
                            } else {
                                d.classList.remove('chapter-progress__dot--passed');
                            }
                        });
                        dot.classList.add('chapter-progress__dot--active');
                    }
                });
            }, { threshold: 0.3 });

            this.chapters.forEach(chapter => observer.observe(chapter));
        }
    }

    // ============================================
    // Personality Toggle - Calm ↔ Energetic
    // ============================================
    class PersonalityToggle {
        constructor() {
            this.currentPersonality = localStorage.getItem('theme_personality') || 'calm';
            this.init();
        }

        init() {
            // Apply saved personality
            document.body.setAttribute('data-personality', this.currentPersonality);

            // Create toggle button (only if setting enabled)
            if (document.body.dataset.showPersonalityToggle !== 'false') {
                this.createToggle();
            }
        }

        createToggle() {
            const toggle = document.createElement('button');
            toggle.className = 'personality-toggle';
            toggle.setAttribute('aria-label', 'Toggle theme personality');
            toggle.innerHTML = `
                <span class="personality-toggle__indicator"></span>
                <span class="personality-toggle__label">${this.currentPersonality === 'calm' ? 'Calm' : 'Energetic'}</span>
            `;

            toggle.addEventListener('click', () => this.toggle());
            document.body.appendChild(toggle);
            this.toggleButton = toggle;
        }

        toggle() {
            this.currentPersonality = this.currentPersonality === 'calm' ? 'energetic' : 'calm';
            document.body.setAttribute('data-personality', this.currentPersonality);
            localStorage.setItem('theme_personality', this.currentPersonality);

            // Update label
            if (this.toggleButton) {
                this.toggleButton.querySelector('.personality-toggle__label').textContent =
                    this.currentPersonality === 'calm' ? 'Calm' : 'Energetic';
            }
        }
    }

    // Export for external use
    window.AntiGravityEffects = {
        MagneticCursor,
        ParallaxEngine,
        ScrollReveal,
        PremiumProductCards,
        GradientMesh,
        CinematicHeadlines,
        ProductFocusMode,
        ChapterProgress,
        PersonalityToggle,
        config
    };

})();
